<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { X, ArrowRight, ChevronLeft, ChevronDown, ChevronUp, ListChecks } from 'lucide-vue-next';
import robotPoint from '@/assets/ai-robot/robot-point-md.webp';
import robotCelebrate from '@/assets/ai-robot/robot-celebrate-md.webp';
import robotStill from '@/assets/ai-robot/robot-still-md.webp';
import { useGuideAvailability } from './useGuideAvailability';
import { activeFlow, activeStep, goToStep, parentFlow, startWalkthrough, stepIndex, stopWalkthrough } from './walkthrough';
import type { WalkStep } from './walkthrough';
import { runStepCheck, runStepCount } from './walkthroughChecks';

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();

const lang = computed(() => (locale.value === 'km' ? 'km' : 'en'));
const onPage = computed(() => {
  const step = activeStep.value;
  return !!step && (step.route === route.name || !!step.alsoOn?.includes(String(route.name)));
});
const isLast = computed(() => !!activeFlow.value && stepIndex.value === activeFlow.value.steps.length - 1);
const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// While a step is shown on its page, keep watching the page: highlight the target as soon
// as it appears (pages and dialogs render late), and move on by itself once the step's
// `waitFor` element appears (e.g. the form went to its next step).
let highlighted: Element | null = null;
let watchTimer: number | undefined;

const clearHighlight = () => {
  window.clearInterval(watchTimer);
  highlighted?.classList.remove('mes-guide-target');
  highlighted = null;
};

const watchPage = () => {
  const step = activeStep.value;
  if (!step || !onPage.value) return;
  watchTimer = window.setInterval(() => {
    if (step.waitFor && document.querySelector(step.waitFor)) {
      next();
      return;
    }
    const el = step.target ? document.querySelector(step.target) : null;
    if (el === highlighted) return;
    highlighted?.classList.remove('mes-guide-target');
    highlighted = el;
    if (!el) return;
    el.classList.add('mes-guide-target');
    el.scrollIntoView({ block: 'center', behavior: reducedMotion ? 'auto' : 'smooth' });
  }, 300);
};

// The user (or a page, like the setup wizard) went on to the next step's page: follow them.
watch(
  () => route.name,
  (name) => {
    const nextStep = activeFlow.value?.steps[stepIndex.value + 1];
    if (!onPage.value && nextStep && (nextStep.route === name || nextStep.alsoOn?.includes(String(name)))) goToStep(stepIndex.value + 1);
  },
);

watch(
  [activeStep, () => route.name],
  () => {
    clearHighlight();
    watchPage();
  },
  { immediate: true },
);

onBeforeUnmount(clearHighlight);

// After the last step the robot celebrates for a moment, then the card closes. A sub-guide
// ("Show me step by step") goes back to the guide it was started from instead.
const finishedTitle = ref('');
let finishTimer: number | undefined;
// Steps actually shown in this guide run; 0 at the end means the plan has none of them.
let shownSteps = 0;
const finishedUnavailable = ref(false);
watch(activeFlow, (flow, prev) => {
  // a fresh start (step 1) resets; returning to a parent guide mid-way keeps counting
  if (flow?.id !== prev?.id && stepIndex.value === 0) shownSteps = 0;
});
const next = () => {
  if (!isLast.value) return goToStep(stepIndex.value + 1);
  if (parentFlow.value) return goToStep(stepIndex.value + 1);
  finishedUnavailable.value = shownSteps === 0;
  finishedTitle.value = activeFlow.value?.title[lang.value] ?? '';
  stopWalkthrough();
  window.clearTimeout(finishTimer);
  finishTimer = window.setTimeout(() => (finishedTitle.value = ''), 2600);
};
onBeforeUnmount(() => window.clearTimeout(finishTimer));

// Steps the school's plan doesn't include (e.g. placement tests, payments) are skipped
// before they are shown; the next step says which ones were skipped.
const { stepAvailable } = useGuideAvailability();
const skippedPages = ref<string[]>([]);
watch(
  activeStep,
  (step) => {
    if (!step) {
      skippedPages.value = [];
    } else if (!stepAvailable(step)) {
      skippedPages.value = [...skippedPages.value, step.page[lang.value]];
      next();
    } else {
      shownSteps++;
    }
  },
  { immediate: true },
);
// Required steps (step.check): "Done, next" stays locked until the API shows the thing
// was created; re-checked every few seconds while the user is on the step's page.
// already = the record existed when the step opened; unknown = the check failed (never blocks)
const checkState = ref<'none' | 'checking' | 'todo' | 'done' | 'already' | 'unknown'>('none');
let checkTimer: number | undefined;
// doneWhen reads the page itself (free, every second). An API check backs off
// (3 s → 30 s), pauses while the tab is hidden, and runs again shortly after the user
// clicks something (e.g. Save in a form), so it follows what the user does.
const CHECK_DELAYS = [3000, 5000, 10000, 20000, 30000];
let checkAttempt = 0;
const scheduleCheck = (step: WalkStep, delay: number) => {
  window.clearTimeout(checkTimer);
  if (document.hidden && !step.doneWhen) return; // resumes on visibilitychange
  checkTimer = window.setTimeout(() => runCheck(step), delay);
};
// countUp steps compare with the count taken when the step opened.
let baseline: { step: WalkStep; count: number | null } | null = null;
const countUpDone = async (step: WalkStep): Promise<boolean | null> => {
  if (baseline?.step !== step) baseline = { step, count: await runStepCount(step.countUp!) };
  if (baseline.count === null) return null; // unknown count: never block
  const now = await runStepCount(step.countUp!);
  return now === null ? null : now > baseline.count;
};
const runCheck = async (step: WalkStep) => {
  const ok = step.doneWhen
    ? !!document.querySelector(step.doneWhen)
    : step.countUp
      ? await countUpDone(step)
      : await runStepCheck(step.check!);
  if (activeStep.value !== step) return;
  const first = checkState.value === 'checking';
  checkState.value = ok === null ? 'unknown' : ok ? (first ? 'already' : 'done') : 'todo';
  if (ok !== false || !onPage.value) return;
  scheduleCheck(step, step.doneWhen ? 1000 : CHECK_DELAYS[Math.min(checkAttempt++, CHECK_DELAYS.length - 1)]);
};
const recheckSoon = (delay: number) => {
  const step = activeStep.value;
  if ((!step?.check && !step?.countUp) || checkState.value !== 'todo' || !onPage.value) return;
  checkAttempt = 0;
  scheduleCheck(step, delay);
};
const onUserClick = () => recheckSoon(1500);
const onVisibility = () => {
  if (!document.hidden) recheckSoon(0);
};
document.addEventListener('pointerup', onUserClick, true);
document.addEventListener('visibilitychange', onVisibility);
onBeforeUnmount(() => {
  document.removeEventListener('pointerup', onUserClick, true);
  document.removeEventListener('visibilitychange', onVisibility);
});
let checkedStep: WalkStep | null = null;
watch(
  [activeStep, onPage],
  () => {
    window.clearTimeout(checkTimer);
    const step = activeStep.value;
    if (!step?.check && !step?.doneWhen && !step?.countUp) {
      checkState.value = 'none';
      return;
    }
    // a new step starts fresh; coming back to the same step's page keeps its result
    if (step !== checkedStep || !['done', 'already'].includes(checkState.value)) checkState.value = 'checking';
    checkedStep = step;
    checkAttempt = 0;
    void runCheck(step);
  },
  { immediate: true },
);
onBeforeUnmount(() => window.clearTimeout(checkTimer));
const nextLocked = computed(() => checkState.value === 'todo' || checkState.value === 'checking');

// Buttons: moving on by hand clears the "skipped" note.
const goNext = () => {
  if (nextLocked.value) return;
  skippedPages.value = [];
  next();
};
// Optional required steps (e.g. a custom report policy) can be left on purpose.
const skipOptional = () => {
  skippedPages.value = [];
  next();
};
const goBack = () => {
  skippedPages.value = [];
  goToStep(stepIndex.value - 1);
};

const nextLabel = computed(() => {
  if (!isLast.value) return t('ai_guide_done_next');
  return parentFlow.value ? t('ai_guide_back_to', { guide: parentFlow.value.title[lang.value] }) : t('ai_guide_finish');
});

// Pages the user can't open: the router guard sends them to /forbidden (role) or a plan
// page (subscription), or stops the navigation. Say so, go back, and skip the step.
const noAccess = ref(false);
let skipTimer: number | undefined;
const skip = () => {
  window.clearTimeout(skipTimer);
  noAccess.value = false;
  next();
};
watch(activeStep, () => {
  window.clearTimeout(skipTimer);
  noAccess.value = false;
});
onBeforeUnmount(() => window.clearTimeout(skipTimer));

const takeMeThere = async () => {
  const step = activeStep.value;
  if (!step) return;
  const from = route.fullPath;
  await router.push({ name: step.route, query: step.query }).catch(() => {});
  if (onPage.value || activeStep.value !== step) return;
  if (route.fullPath !== from) router.back();
  noAccess.value = true;
  skipTimer = window.setTimeout(skip, 4500);
};

// Text size for easier reading: normal, large, extra large (remembered in this browser).
const TEXT_SIZES = [1, 1.2, 1.4];
const SIZE_KEY = 'mes-guide-text-size';
const readSize = () => {
  try {
    return Math.min(Math.max(Number(localStorage.getItem(SIZE_KEY)) || 0, 0), TEXT_SIZES.length - 1);
  } catch {
    return 0;
  }
};
const textSize = ref(readSize());
const cycleTextSize = () => {
  textSize.value = (textSize.value + 1) % TEXT_SIZES.length;
  try {
    localStorage.setItem(SIZE_KEY, String(textSize.value));
  } catch { /* storage blocked: size lasts until reload */ }
};

// The card can be folded to its title line and dragged by it, so it never hides a form's buttons.
const collapsed = ref(false);
const pos = ref<{ x: number; y: number } | null>(null); // top-left; null = bottom-right corner
const card = ref<HTMLElement | null>(null);
const cardStyle = computed(() => ({
  '--guide-scale': TEXT_SIZES[textSize.value],
  ...(pos.value ? { left: `${pos.value.x}px`, top: `${pos.value.y}px`, right: 'auto', bottom: 'auto' } : {}),
}));
let drag: { dx: number; dy: number } | null = null;
const onDragStart = (e: PointerEvent) => {
  if ((e.target as HTMLElement).closest('button') || !card.value) return;
  const r = card.value.getBoundingClientRect();
  drag = { dx: e.clientX - r.left, dy: e.clientY - r.top };
  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
};
const onDragMove = (e: PointerEvent) => {
  if (!drag || !card.value) return;
  const r = card.value.getBoundingClientRect();
  pos.value = {
    x: Math.min(Math.max(8, e.clientX - drag.dx), window.innerWidth - r.width - 8),
    y: Math.min(Math.max(8, e.clientY - drag.dy), window.innerHeight - r.height - 8),
  };
};
const onDragEnd = () => (drag = null);
</script>

<template>
  <!-- On <body>, above open dialogs (z-50): a modal dialog teleports there too, and the
       layout's stacking context would otherwise put the card behind it. Pointer-downs stop
       here so a dialog doesn't treat them as "click outside" and close; mousedown keeps
       focus in the dialog so its focus trap doesn't fight the card's buttons. -->
  <Teleport to="body">
  <Transition name="guide-card">
    <div v-if="finishedTitle && !activeFlow" class="guide-card" role="status" @pointerdown.stop @mousedown.prevent>
      <img :src="reducedMotion || finishedUnavailable ? robotStill : robotCelebrate" alt="" class="guide-card-robot" />
      <div class="guide-card-body">
        <div class="guide-card-head"><span>{{ finishedTitle }}</span></div>
        <p class="guide-card-text guide-card-text--done">{{ finishedUnavailable ? t('ai_guide_not_available') : t('ai_guide_all_done') }}</p>
      </div>
    </div>
    <div
      v-else-if="activeFlow && activeStep"
      ref="card"
      class="guide-card"
      :class="{ 'guide-card--collapsed': collapsed }"
      :style="cardStyle"
      role="dialog"
      :aria-label="activeFlow.title[lang]"
      @pointerdown.stop
      @mousedown.prevent
    >
      <img :src="reducedMotion ? robotStill : robotPoint" alt="" class="guide-card-robot" />
      <div class="guide-card-body">
        <div class="guide-card-head" @pointerdown="onDragStart" @pointermove="onDragMove" @pointerup="onDragEnd" @pointercancel="onDragEnd">
          <span>{{ activeFlow.title[lang] }} · {{ stepIndex + 1 }}/{{ activeFlow.steps.length }}</span>
          <span class="guide-card-tools">
            <button type="button" class="guide-card-close guide-card-size" :aria-label="t('ai_guide_text_size')" :title="t('ai_guide_text_size')" @click="cycleTextSize">
              A<span class="guide-card-size-plus">{{ '+'.repeat(textSize) || '' }}</span>
            </button>
            <button type="button" class="guide-card-close" :aria-label="collapsed ? t('ai_guide_expand') : t('ai_guide_collapse')" :aria-expanded="!collapsed" @click="collapsed = !collapsed">
              <ChevronUp v-if="collapsed" class="w-4 h-4" />
              <ChevronDown v-else class="w-4 h-4" />
            </button>
            <button type="button" class="guide-card-close" :aria-label="t('ai_guide_exit')" @click="stopWalkthrough">
              <X class="w-4 h-4" />
            </button>
          </span>
        </div>
        <template v-if="!collapsed">
          <p v-if="noAccess" class="guide-card-text guide-card-text--warn">
            {{ t('ai_guide_no_access', { page: activeStep.page[lang] }) }}
          </p>
          <p v-else-if="!onPage" class="guide-card-text">
            {{ t('ai_guide_next_stop') }} <strong>{{ activeStep.page[lang] }}</strong>
          </p>
          <template v-else>
            <p v-if="skippedPages.length" class="guide-card-skipped">
              {{ t('ai_guide_skipped', { pages: skippedPages.join(', ') }) }}
            </p>
            <p class="guide-card-text guide-card-text--goal">{{ activeStep.text[lang] }}</p>
            <ol v-if="activeStep.how" class="guide-card-how">
              <li v-for="line in activeStep.how[lang]" :key="line">{{ line }}</li>
            </ol>
            <p v-if="checkState === 'todo'" class="guide-card-check guide-card-check--todo" role="status">{{ t('ai_guide_waiting') }}</p>
            <p v-else-if="checkState === 'done'" class="guide-card-check guide-card-check--done" role="status">✓ {{ t('ai_guide_step_done') }}</p>
            <p v-else-if="checkState === 'already'" class="guide-card-check guide-card-check--done" role="status">✓ {{ t('ai_guide_already_done') }}</p>
          </template>
          <div class="guide-card-actions">
            <button v-if="stepIndex > 0" type="button" class="guide-btn guide-btn--ghost" @click="goBack">
              <ChevronLeft class="w-4 h-4" /> {{ t('ai_guide_back') }}
            </button>
            <button v-if="noAccess" type="button" class="guide-btn guide-btn--primary" @click="skip">
              {{ t('ai_guide_skip') }} <ArrowRight class="w-4 h-4" />
            </button>
            <button v-else-if="!onPage" type="button" class="guide-btn guide-btn--primary" @click="takeMeThere">
              {{ t('ai_guide_take_me') }} <ArrowRight class="w-4 h-4" />
            </button>
            <template v-else>
              <button v-if="activeStep.guide" type="button" class="guide-btn guide-btn--soft" @click="startWalkthrough(activeStep.guide, true)">
                <ListChecks class="w-4 h-4" /> {{ t('ai_guide_step_by_step') }}
              </button>
              <button v-if="activeStep.optional && nextLocked" type="button" class="guide-btn guide-btn--ghost" @click="skipOptional">{{ t('ai_guide_skip_step') }}</button>
              <button type="button" class="guide-btn guide-btn--primary" :disabled="nextLocked" @click="goNext">{{ nextLabel }}</button>
            </template>
          </div>
        </template>
      </div>
    </div>
  </Transition>
  </Teleport>
</template>

<style scoped>
.guide-card {
  pointer-events: auto; /* an open modal sets pointer-events: none on <body> */
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 60;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  width: min(calc(400px * var(--guide-scale, 1)), calc(100vw - 32px));
}
.guide-card-robot {
  width: 64px;
  height: 91px;
  flex-shrink: 0;
  object-fit: contain;
}
.guide-card-body {
  flex: 1;
  min-width: 0;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: 12px 14px;
  border-radius: 16px 16px 16px 4px;
  background: #fff;
  border: 1px solid #dbeafe;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
}
:root.dark .guide-card-body {
  background: #1e293b;
  border-color: #334155;
}
.guide-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: calc(11px * var(--guide-scale, 1));
  font-weight: 600;
  color: #2563eb;
}
.guide-card-head {
  cursor: move;
  touch-action: none;
  user-select: none;
}
.guide-card-size {
  min-width: 24px;
  font-size: 12px !important;
  font-weight: 700;
  line-height: 1;
}
.guide-card-size-plus {
  font-size: 10px;
}
.guide-card-tools {
  display: inline-flex;
  gap: 2px;
}
.guide-card--collapsed .guide-card-robot {
  width: 40px;
  height: 57px;
}
.guide-card-close {
  color: #94a3b8;
  border-radius: 6px;
  padding: 2px;
}
.guide-card-close:hover {
  color: #475569;
  background: #f1f5f9;
}
.guide-card-text {
  margin: 6px 0 10px;
  font-size: calc(13px * var(--guide-scale, 1));
  line-height: 1.5;
  color: #334155;
}
:root.dark .guide-card-text {
  color: #e2e8f0;
}
.guide-card-text--done {
  margin-bottom: 2px;
  font-weight: 600;
}
.guide-card-skipped {
  margin: 6px 0 0;
  padding: 4px 8px;
  border-radius: 6px;
  background: #fef3c7;
  color: #92400e;
  font-size: calc(11.5px * var(--guide-scale, 1));
}
:root.dark .guide-card-skipped {
  background: rgba(245, 158, 11, 0.15);
  color: #fcd34d;
}
.guide-card-check {
  margin: -2px 0 10px;
  font-size: calc(12px * var(--guide-scale, 1));
  font-weight: 600;
}
.guide-card-check--todo {
  color: #b45309;
}
.guide-card-check--done {
  color: #15803d;
}
:root.dark .guide-card-check--todo {
  color: #fbbf24;
}
:root.dark .guide-card-check--done {
  color: #4ade80;
}
.guide-btn--primary:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.guide-card-text--goal {
  font-weight: 600;
}
.guide-card-text--warn {
  color: #b45309;
}
:root.dark .guide-card-text--warn {
  color: #fbbf24;
}
.guide-card-how {
  margin: -4px 0 10px;
  padding-left: 18px;
  list-style: decimal;
  font-size: calc(12.5px * var(--guide-scale, 1));
  line-height: 1.5;
  color: #475569;
}
.guide-card-how li + li {
  margin-top: 3px;
}
:root.dark .guide-card-how {
  color: #cbd5e1;
}
.guide-card-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}
.guide-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 9px;
  font-size: calc(12px * var(--guide-scale, 1));
  font-weight: 600;
}
.guide-btn--primary {
  background: #2563eb;
  color: #fff;
}
.guide-btn--primary:hover {
  background: #1d4ed8;
}
.guide-btn--soft {
  background: #eff6ff;
  color: #1d4ed8;
}
.guide-btn--soft:hover {
  background: #dbeafe;
}
:root.dark .guide-btn--soft {
  background: rgba(37, 99, 235, 0.18);
  color: #93c5fd;
}
.guide-btn--ghost {
  color: #475569;
}
.guide-btn--ghost:hover {
  background: #f1f5f9;
}
:root.dark .guide-btn--ghost {
  color: #cbd5e1;
}
:root.dark .guide-btn--ghost:hover {
  background: #334155;
}
.guide-card-enter-active,
.guide-card-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.guide-card-enter-from,
.guide-card-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

/* The button the robot points at (class is set on page elements, so not scoped). */
:global(.mes-guide-target) {
  outline: 3px solid #2563eb !important;
  outline-offset: 3px;
  animation: mes-guide-pulse 1.4s ease-out infinite;
}
@keyframes mes-guide-pulse {
  0% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.55); }
  100% { box-shadow: 0 0 0 14px rgba(37, 99, 235, 0); }
}
@media (prefers-reduced-motion: reduce) {
  :global(.mes-guide-target) {
    animation: none;
  }
}
</style>
